import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import { UnitDetailsFormFields } from "@/modules/property-tracks/hooks/rental-financial-proposal/multi-step-form.hook";
import { Box, Divider, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

export default function UnitDetailsForm({
  form,
}: {
  form: UseFormReturn<UnitDetailsFormFields>;
}) {
  const [finishingCost, furnishingCost, pricePerMeter, unitArea] = [
    Number(
      useWatch({
        control: form.control,
        name: "finishingCost",
      })
    ) || 0,
    Number(
      useWatch({
        control: form.control,
        name: "furnishingCost",
      })
    ) || 0,
    Number(
      useWatch({
        control: form.control,
        name: "pricePerMeter",
      })
    ) || 0,
    Number(
      useWatch({
        control: form.control,
        name: "unitAreaSnapshot",
      })
    ) || 0,
  ];

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    form.setValue("finishingCost", pricePerMeter * unitArea);
  }, [pricePerMeter, unitArea, form]);

  const sellingPrice: number = finishingCost + furnishingCost;

  return (
    <Box>
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Unit details
        </Typography>
        <Controller
          control={form.control}
          name={"clientNameSnapshot"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Client Name"
              placeholder="Client Name"
              disabled={true}
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"compoundNameSnapshot"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Compound"
              placeholder="Compound"
              disabled
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"propertyTypeNameSnapshot"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              title="Property Type"
              placeholder="Property Type"
              disabled
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"unitAreaSnapshot"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Unit Area"
              placeholder="Unit Area"
              disabled={true}
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>m²</Typography>,
                },
              }}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"pricePerMeter"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Price per meter"
              placeholder="Price per meter"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>EGP</Typography>,
                },
              }}
            />
          )}
        />
      </Box>
      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.md,
        })}
      >
        <Box>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Selling price
          </Typography>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
          >
            The sum of finishing & furnishing.
          </Typography>
        </Box>
        <Controller
          control={form.control}
          name={"finishingCost"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Finishing"
              placeholder="Finishing"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>EGP</Typography>,
                },
              }}
            />
          )}
        />
        <Controller
          control={form.control}
          name={"furnishingCost"}
          render={({ field, fieldState }) => (
            <StyledTextFieldWithTitle
              {...field}
              formatNumeric
              title="Furnishing"
              placeholder="Furnishing"
              fullWidth
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  endAdornment: <Typography>EGP</Typography>,
                },
              }}
            />
          )}
        />

        <StyledTextFieldWithTitle
          formatNumeric
          title="Selling Price"
          placeholder="Selling Price"
          fullWidth
          size="small"
          disabled={true}
          value={sellingPrice.toLocaleString()}
          slotProps={{
            input: {
              endAdornment: <Typography>EGP</Typography>,
            },
          }}
        />
      </Box>
    </Box>
  );
}
